import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faBriefcase, faCheckCircle, faChartPie, faChartBar,
  faSyncAlt, faList, faEye, faEnvelope, faPhone, faMapMarkerAlt,
  faIndustry, faCalendarAlt, faMoneyBillWave, faUserTie,
  faCheckCircle as faCheck, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import { PartenairesContext } from '../layout/CunstomPartenaires';

// ============================================================
// UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }

  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// CACHE DEXIE (24h) avec possibilité de récupérer le cache périmé
// ============================================================
const db = new Dexie('DashboardEntreprisesCache');
db.version(1).stores({ stats: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCached(key, allowExpired = false) {
  try {
    const entry = await db.stats.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCached(key, data) {
  await db.stats.put({ key, data, timestamp: Date.now() });
}
async function getStaleCache(key) {
  return getCached(key, true);
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES EN COURS (Map globale)
// ============================================================
const pendingFetches = new Map();

const API_URL = `${API_CONFIG2.apiUrl}entreprises/api_dashboard_entreprise.php`;

// ============================================================
// HOOK COMPTEUR ANIMÉ (inchangé)
// ============================================================
const useCountAnimation = (target, duration = 800, startDelay = 0) => {
  const [current, setCurrent] = useState(0);
  const frame = useRef(null);

  useEffect(() => {
    if (target === undefined || target === null) return;
    let start = null;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      setCurrent(Math.floor(progress * target));
      if (progress < 1) {
        frame.current = requestAnimationFrame(step);
      }
    };
    const timeout = setTimeout(() => {
      frame.current = requestAnimationFrame(step);
    }, startDelay);
    return () => {
      clearTimeout(timeout);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [target, duration, startDelay]);

  return current;
};

// ============================================================
// PARTICULES RÉSEAU (inchangé)
// ============================================================
const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const animFrame = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width, height;
    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initParticles();
    };

    const initParticles = () => {
      particles.current = Array.from({ length: 60 }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 2 + 1,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const pts = particles.current;
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(59, 130, 246, 0.25)';
        ctx.fill();

        for (let j = i + 1; j < pts.length; j++) {
          const p2 = pts[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 140) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(59, 130, 246, ${0.12 * (1 - dist / 140)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      animFrame.current = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    animFrame.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      if (animFrame.current) cancelAnimationFrame(animFrame.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed', top: 0, left: 0, zIndex: 0,
        pointerEvents: 'none', opacity: 0.6,
      }}
    />
  );
};

// ============================================================
// CARTE KPI ANIMÉE (inchangée)
// ============================================================
const KpiCard = ({ icon, value, label, delay = 0 }) => {
  const animatedValue = useCountAnimation(value, 1000, delay + 200);
  return (
    <div className="glass-card kpi-card fade-up" style={{ animationDelay: `${delay}ms` }}>
      <div className="kpi-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="kpi-content">
        <h3>{animatedValue}</h3>
        <p>{label}</p>
      </div>
    </div>
  );
};

// ============================================================
// COMPOSANT PRINCIPAL AVEC CONTEXTE
// ============================================================
const DashboardEntreprises = () => {
  const context = useContext(PartenairesContext) || {};
  const { refreshKey, setSource } = context;

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dataSource, setDataSource] = useState('cache');
  const abortControllerRef = useRef(null);
  const cardIndex = useRef(0);
  const isMounted = useRef(true);

  // ============================================================
  // GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      if (isMounted.current) setToast(prev => ({ ...prev, show: false }));
    }, duration);
  }, []);

  // ============================================================
  // RÉCUPÉRATION DES STATISTIQUES AVEC DÉDOUBLONNAGE + ANNULATION
  // ============================================================
  const fetchStats = useCallback(async (forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = 'dashboard_entreprise';
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est en cours, on l'attend
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setStats(result);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
          setError('');
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');

    const fetchPromise = (async () => {
      try {
        // 1. Cache (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = await getCached(cacheKey);
          if (cached && isMounted.current) {
            setStats(cached);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        // 2. Requête réseau
        const response = await fetch(API_URL, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await getStaleCache(cacheKey);
          if (stale && isMounted.current) {
            setStats(stale);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache périmé', 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchStats(forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleCache(cacheKey);
          if (stale && isMounted.current) {
            setStats(stale);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast(`Erreur réseau – Utilisation du cache`, 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          if (isMounted.current) {
            setStats(data.data);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await setCached(cacheKey, data.data);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
            setError('');
          }
          return data.data;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleCache(cacheKey);
        if (stale && isMounted.current) {
          setStats(stale);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          setError(err.message);
          showToast(err.message, 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [setSource, showToast]);

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchStats();
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // RÉACTION AU REFRESH GLOBAL (via contexte)
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchStats(true);
    }
  }, [refreshKey, fetchStats]);

  // ============================================================
  // GESTIONNAIRE DE RAFRAÎCHISSEMENT LOCAL
  // ============================================================
  const handleRefresh = () => {
    if (!loading && !refreshing) {
      fetchStats(true);
    }
  };

  // ============================================================
  // AFFICHAGE DES ÉTATS DE CHARGEMENT / ERREUR
  // ============================================================
  if (loading && !stats) {
    return (
      <>
        <ParticleCanvas />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-secondary mt-2">Chargement du tableau de bord...</p>
        </div>
      </>
    );
  }

  if (error && !stats) {
    return (
      <>
        <ParticleCanvas />
        <div className="text-center py-5 text-danger">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <p>{error}</p>
          <button className="monetra-btn primary" onClick={() => fetchStats(true)}>Réessayer</button>
        </div>
      </>
    );
  }

  if (!stats) return null;

  const maxCount = Math.max(...stats.evolutionOffres.map(m => m.count), 1);
  const colors = ['#3b82f6', '#60a5fa', '#93c5fd', '#1d4ed8', '#2563eb', '#7c3aed'];

  cardIndex.current = 0;
  const getNextDelay = () => {
    cardIndex.current += 1;
    return cardIndex.current * 80;
  };

  // ============================================================
  // RENDU AVEC STYLES RESPONSIFS OPTIMISÉS
  // ============================================================
  return (
    <>
      <ParticleCanvas />
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-glow: rgba(59, 130, 246, 0.35);
          --bg-primary: #0b1428;
          --card-bg: rgba(20, 40, 80, 0.5);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 18px;
          --shadow: 0 12px 40px rgba(0,0,0,0.5);
        }

        .dashboard-entreprise {
          position: relative;
          z-index: 1;
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          background: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
        }

        .fade-up {
          opacity: 0;
          animation: fadeInUp 0.5s ease forwards;
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 1.5vw, 1.5rem);
          box-shadow: var(--shadow);
          transition: all 0.25s ease;
          height: 100%;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.3);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.08);
          transform: translateY(-2px);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 18px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: 0.3s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          min-height: 44px;
          touch-action: manipulation;
          white-space: nowrap;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: white;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 6px 24px rgba(59,130,246,0.4);
        }

        .kpi-card {
          display: flex;
          align-items: center;
          gap: clamp(0.8rem, 1.5vw, 1.2rem);
        }
        .kpi-icon {
          width: clamp(44px, 6vw, 54px);
          height: clamp(44px, 6vw, 54px);
          border-radius: 16px;
          background: linear-gradient(135deg, rgba(59,130,246,0.2), rgba(29,78,216,0.1));
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(1.2rem, 2vw, 1.6rem);
          color: var(--accent-blue);
          backdrop-filter: blur(4px);
          flex-shrink: 0;
        }
        .kpi-content h3 {
          font-size: clamp(1.4rem, 3vw, 2rem);
          font-weight: 700;
          margin: 0;
          line-height: 1.2;
          background: linear-gradient(180deg, #fff, #a5b4fc);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .kpi-content p {
          margin: 0;
          color: var(--text-secondary);
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }

        .grid-2col {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(clamp(160px, 22vw, 220px), 1fr));
          gap: clamp(12px, 2vw, 20px);
          margin-bottom: clamp(1rem, 2vw, 1.5rem);
        }
        .grid-2col-wide {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: clamp(12px, 2vw, 20px);
          margin-bottom: clamp(1rem, 2vw, 1.5rem);
        }
        @media (max-width: 992px) {
          .grid-2col-wide {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 768px) {
          .dashboard-entreprise { padding: 12px; }
          .grid-2col {
            grid-template-columns: 1fr 1fr;
          }
        }
        @media (max-width: 480px) {
          .grid-2col {
            grid-template-columns: 1fr;
          }
        }

        .chart-bar {
          display: flex;
          align-items: flex-end;
          gap: 4px;
          height: clamp(100px, 20vh, 140px);
          margin-top: 10px;
        }
        .chart-bar-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }
        .chart-bar-fill {
          width: 100%;
          border-radius: 8px 8px 4px 4px;
          min-height: 4px;
          height: 0;
          animation: growBar 0.8s ease forwards;
          box-shadow: 0 0 8px rgba(59,130,246,0.3);
        }
        @keyframes growBar {
          to { height: var(--target-height); }
        }
        .chart-bar-fill:hover {
          opacity: 1;
          filter: brightness(1.2);
        }
        .chart-bar-label {
          font-size: clamp(0.55rem, 0.8vw, 0.7rem);
          color: var(--text-secondary);
          text-align: center;
        }

        .list-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-size: clamp(0.85rem, 1vw, 1rem);
        }
        .list-item:last-child { border-bottom: none; }

        .badge-statut {
          display: inline-block;
          padding: 2px 12px;
          border-radius: 20px;
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
          font-weight: 500;
        }
        .badge-statut.ouverte { background: rgba(16,185,129,0.15); color: #10b981; }
        .badge-statut.fermee { background: rgba(239,68,68,0.15); color: #ef4444; }
        .badge-statut.en-cours { background: rgba(245,158,11,0.15); color: #f59e0b; }
        .badge-contrat {
          padding: 2px 10px;
          background: rgba(255,255,255,0.06);
          border-radius: 12px;
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
          color: var(--text-secondary);
        }

        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .mb-3 { margin-bottom: clamp(1rem, 2vw, 1.5rem); }
        .mt-3 { margin-top: clamp(1rem, 2vw, 1.5rem); }
        .text-secondary { color: var(--text-secondary); }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .text-accent { color: var(--accent-blue); }
        .me-1 { margin-right: 0.25rem; }
        .me-2 { margin-right: 0.5rem; }

        .toast-container {
          position: fixed;
          top: clamp(12px, 2vh, 24px);
          right: clamp(12px, 2vw, 24px);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10,15,31,0.85);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59,130,246,0.2);
          border-radius: var(--radius);
          padding: 14px 20px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5);
          animation: slideIn 0.4s ease;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .toast-item.success { border-left: 4px solid #10b981; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
        }
        .btn-close-custom:hover { color: var(--text-primary); }
      `}</style>

      <div className="dashboard-entreprise">
        {/* Header avec badge source */}
        <div className="flex-between mb-3 fade-up" style={{ animationDelay: '0ms' }}>
          <div>
            <h1 className="fw-bold mb-1" style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)' }}>
              📊 Tableau de bord Entreprises & Offres
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Analyse des données consolidées
            </p>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn primary"
              onClick={handleRefresh}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} />
              {refreshing ? 'Actualisation...' : 'Actualiser'}
            </button>
          </div>
        </div>

        {/* Cartes KPI */}
        <div className="grid-2col mb-3">
          <KpiCard icon={faBuilding} value={stats.totalEntreprises} label="Entreprises" delay={getNextDelay()} />
          <KpiCard icon={faBriefcase} value={stats.totalOffres} label="Offres totales" delay={getNextDelay()} />
          <KpiCard icon={faCheckCircle} value={stats.totalOffresOuvertes} label="Offres ouvertes" delay={getNextDelay()} />
          <KpiCard
            icon={faChartPie}
            value={stats.topEntreprises.length > 0 ? stats.topEntreprises[0].nb_offres : 0}
            label={`Top entreprise (${stats.topEntreprises.length > 0 ? stats.topEntreprises[0].nomEntreprise : '—'})`}
            delay={getNextDelay()}
          />
        </div>

        {/* Graphique + Top entreprises */}
        <div className="grid-2col-wide mb-3">
          <div className="glass-card fade-up" style={{ animationDelay: `${getNextDelay()}ms` }}>
            <h5 className="d-flex align-items-center gap-2 text-accent" style={{ fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)' }}>
              <FontAwesomeIcon icon={faChartBar} />
              Évolution des offres (12 mois)
            </h5>
            <div className="chart-bar">
              {stats.evolutionOffres.map((item, idx) => {
                const heightPercent = Math.max((item.count / maxCount) * 100, 4);
                return (
                  <div key={idx} className="chart-bar-item">
                    <div
                      className="chart-bar-fill"
                      style={{
                        '--target-height': `${heightPercent}px`,
                        background: colors[idx % colors.length],
                      }}
                    />
                    <span className="chart-bar-label">{item.month}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="glass-card fade-up" style={{ animationDelay: `${getNextDelay()}ms` }}>
            <h5 className="d-flex align-items-center gap-2 text-accent" style={{ fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)' }}>
              <FontAwesomeIcon icon={faBuilding} />
              Top entreprises
            </h5>
            {stats.topEntreprises.length === 0 ? (
              <p className="text-secondary">Aucune offre</p>
            ) : (
              stats.topEntreprises.map((item, idx) => (
                <div key={idx} className="list-item">
                  <span>{item.nomEntreprise}</span>
                  <span className="badge-contrat">{item.nb_offres}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Secteurs + Contrats + Statuts */}
        <div className="grid-2col-wide mb-3">
          <div className="glass-card fade-up" style={{ animationDelay: `${getNextDelay()}ms` }}>
            <h5 className="d-flex align-items-center gap-2 text-accent" style={{ fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)' }}>
              <FontAwesomeIcon icon={faIndustry} />
              Secteurs d'activité
            </h5>
            {stats.secteurs.length === 0 ? (
              <p className="text-secondary">Aucune entreprise</p>
            ) : (
              stats.secteurs.slice(0, 6).map((item, idx) => (
                <div key={idx} className="list-item">
                  <span>{item.secteur_activite}</span>
                  <span className="badge-contrat">{item.count}</span>
                </div>
              ))
            )}
          </div>
          <div className="glass-card fade-up" style={{ animationDelay: `${getNextDelay()}ms` }}>
            <h5 className="d-flex align-items-center gap-2 text-accent" style={{ fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)' }}>
              <FontAwesomeIcon icon={faUserTie} />
              Types de contrat
            </h5>
            {stats.contrats.length === 0 ? (
              <p className="text-secondary">Aucune offre</p>
            ) : (
              stats.contrats.map((item, idx) => (
                <div key={idx} className="list-item">
                  <span>{item.type_contrat}</span>
                  <span className="badge-contrat">{item.count}</span>
                </div>
              ))
            )}
            <h5 className="mt-3 d-flex align-items-center gap-2 text-accent" style={{ fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)' }}>
              <FontAwesomeIcon icon={faEye} />
              Statuts des offres
            </h5>
            {stats.statuts.length === 0 ? (
              <p className="text-secondary">Aucune offre</p>
            ) : (
              stats.statuts.map((item, idx) => {
                let cls = '';
                if (item.statut === 'Ouverte') cls = 'ouverte';
                else if (item.statut === 'Fermée') cls = 'fermee';
                else cls = 'en-cours';
                return (
                  <div key={idx} className="list-item">
                    <span className={`badge-statut ${cls}`}>{item.statut}</span>
                    <span className="badge-contrat">{item.count}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Dernières offres et entreprises */}
        <div className="grid-2col-wide">
          <div className="glass-card fade-up" style={{ animationDelay: `${getNextDelay()}ms` }}>
            <h5 className="d-flex align-items-center gap-2 text-accent" style={{ fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)' }}>
              <FontAwesomeIcon icon={faList} />
              Dernières offres
            </h5>
            {stats.recentOffres.length === 0 ? (
              <p className="text-secondary">Aucune offre récente</p>
            ) : (
              stats.recentOffres.map(item => (
                <div key={item.id} className="list-item">
                  <div>
                    <div><FontAwesomeIcon icon={faBriefcase} className="me-1 text-secondary" />{item.description.slice(0, 30)}...</div>
                    <div className="small text-secondary">{item.nomEntreprise}</div>
                  </div>
                  <span className="badge-statut ouverte">{item.statut}</span>
                </div>
              ))
            )}
          </div>
          <div className="glass-card fade-up" style={{ animationDelay: `${getNextDelay()}ms` }}>
            <h5 className="d-flex align-items-center gap-2 text-accent" style={{ fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)' }}>
              <FontAwesomeIcon icon={faBuilding} />
              Dernières entreprises
            </h5>
            {stats.recentEntreprises.length === 0 ? (
              <p className="text-secondary">Aucune entreprise récente</p>
            ) : (
              stats.recentEntreprises.map(item => (
                <div key={item.id} className="list-item">
                  <div>
                    <div><FontAwesomeIcon icon={faBuilding} className="me-1 text-secondary" />{item.nomEntreprise}</div>
                    <div className="small text-secondary">{item.secteur_activite}</div>
                  </div>
                  <span className="badge-contrat">{item.email}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheck : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardEntreprises;